import axios from "axios";

import {
    API_BASE_URL,
    API_BASE_STAGE_URL,
    REFRESH_TOKEN_URL,
} from "../../config/constants";

import constants from "./constants";
import { decryptResponse } from "../utils/encryptionUtils";

export class ApiBase {
    constructor(params) {
        this.isRefreshing = false;
        this.failedQueue = [];

        this.instance = axios.create({
            baseURL: API_BASE_URL + API_BASE_STAGE_URL,
            // baseURL: "/api",
            headers: this.handleHeaders(params),
            withCredentials: true, // REQUIRED for cookies
        });

        this.instance.interceptors.response.use(
            (response) => {
                if (response.data && response.data.dataToken) {
                    response.data.data = decryptResponse(response.data);
                }
                return response;
            },
            (error) => this.handleResponseError(error),
        );
    }

    // ========================
    // RESPONSE ERROR HANDLER
    // ========================
    handleResponseError = (error) => {
        const originalRequest = error.config;
        if (
            error.response &&
            error.response.status === 401 &&
            (
                error.response.data.code === constants.ERROR_CODES.TOKEN_EXPIRED ||
                error.response.data.code === constants.ERROR_CODES.TOKEN_MISSING
            ) &&
            !originalRequest._retry
        ) {
            // 🔁 If already refreshing, queue requests
            if (this.isRefreshing) {
                return new Promise((resolve, reject) => {
                    this.failedQueue.push({ resolve, reject });
                })
                    .then(() => {
                        if (originalRequest.url.startsWith("/api")) {
                            originalRequest.url = originalRequest.url.replace("/api", "");
                        }
                        return this.instance(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            this.isRefreshing = true;

            return new Promise((resolve, reject) => {
                this.refreshToken()
                    .then(() => {
                        this.processQueue(null);
                        resolve(this.instance(originalRequest)); // retry
                    })
                    .catch((err) => {
                        this.processQueue(err);
                        this.handleRefreshFailure();
                        reject(err);
                    })
                    .finally(() => {
                        this.isRefreshing = false;
                    });
            });
        }

        this.getErrorResponse(error);
        return Promise.reject(error);
    };

    // ========================
    //  QUEUE HANDLER
    // ========================
    processQueue(error) {
        this.failedQueue.forEach((prom) => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve();
            }
        });
        this.failedQueue = [];
    }

    // ========================
    // REFRESH TOKEN (COOKIE)
    // ========================
    refreshToken() {
        const refreshApi = axios.create({
            baseURL: API_BASE_URL + API_BASE_STAGE_URL,
            // baseURL: "/api",
            withCredentials: true, // 🍪 send cookies
        });

        return refreshApi.post(REFRESH_TOKEN_URL, {});
        // backend sets new cookies
        //  no token handling here
    }

    // ========================
    // REFRESH FAILURE
    // ========================
    handleRefreshFailure() {
        if (
            window.location.pathname !== constants.path.accessDenied &&
            window.location.pathname !== "/" &&
            window.location.pathname !== "/login" &&
            window.location.pathname !== "/forgot" &&
            window.location.pathname !== "/sso-redirect/"
        ) {
            window.location.href = constants.path.accessDenied;
        }
    }

    // ========================
    // ⚠️ GENERAL ERROR HANDLING
    // ========================


    getErrorResponse(error) {
        if (!error || !error.response) return;

        const { status, data } = error.response;
        const errorCode = data && data.code;

        const isAuthError =
            status === 401 ||
            errorCode === constants.ERROR_CODES.TOKEN_MISSING ||
            errorCode === constants.ERROR_CODES.INVALID_TOKEN ||
            errorCode === constants.ERROR_CODES.INVALID_SESSION ||
            errorCode === constants.ERROR_CODES.NOT_AUTHORIZED;

        if (isAuthError) {
            const currentPath = window.location.pathname;

            const ignoredRoutes = [
                "/",
                "/login",
                "/forgot",
                "/sso-redirect/",
                constants.path.accessDenied
            ];

            const shouldRedirect = !ignoredRoutes.includes(currentPath);

            if (shouldRedirect) {
                window.location.href = constants.path.accessDenied;
            }
        }
    }

    handleHeaders = (params) => {
        if (params && params.skipAuth) {
            return constants.defaultRequestHeader;
        } else if (params) {
            return params;
        } else {
            return constants.defaultRequestHeader;
        }
    };
}
